import syncMove from './sync-move';
import { EventEmitter } from 'events';

/**
 * Compare class for creating a swipe comparison between two Mapbox GL maps
 *
 * @class
 * @param {Object} mapA - The first Mapbox GL Map
 * @param {Object} mapB - The second Mapbox GL Map
 * @param {string|HTMLElement} container - An HTML Element or a selector string for the compare container.
 *    It should be a wrapper around the two map Elements.
 * @param {Object} [options={}] - Options object
 * @param {string} [options.orientation=vertical] - Orientation of the compare slider.
 *    `'vertical'` places a vertical slider bar (map A on the left, map B on the right),
 *    `'horizontal'` places a horizontal slider bar (map A on top, map B on bottom).
 * @param {boolean} [options.mousemove=false] - If `true`, the compare slider will move with the cursor;
 *    otherwise the slider must be dragged.
 * @example
 * const compare = new Compare(beforeMap, afterMap, '#wrapper', {
 *   orientation: 'vertical',
 *   mousemove: true
 * });
 * @see https://www.mapbox.com/mapbox-gl-js/example/mapbox-gl-compare/
 */
class Compare {
  constructor(mapA, mapB, container, options = {}) {
    this.options = options;
    this._mapA = mapA;
    this._mapB = mapB;
    this._horizontal = this.options.orientation === 'horizontal';

    // Events
    this._ev = new EventEmitter();

    // Create the swiper element
    this._swiper = document.createElement('div');
    this._swiper.className = this._horizontal
      ? 'compare-swiper-horizontal'
      : 'compare-swiper-vertical';

    // Create the control container
    this._controlContainer = document.createElement('div');
    this._controlContainer.className = this._horizontal
      ? 'mapboxgl-compare mapboxgl-compare-horizontal'
      : 'mapboxgl-compare';

    this._controlContainer.appendChild(this._swiper);

    // Append the compare container to user-supplied container
    if (typeof container === 'string' && document.body.querySelectorAll) {
      const appendTarget = document.body.querySelectorAll(container)[0];
      if (!appendTarget) {
        throw new Error('Cannot find element with specified container selector.');
      }
      appendTarget.appendChild(this._controlContainer);
    } else if (container instanceof Element && container.appendChild) {
      container.appendChild(this._controlContainer);
    } else {
      throw new Error(
        'Invalid container specified. Must be CSS selector or HTML element.'
      );
    }

    // Initial bounds and position
    this._bounds = mapB.getContainer().getBoundingClientRect();
    const swiperPosition = (this._horizontal
      ? this._bounds.height
      : this._bounds.width) / 2;
    this._setPosition(swiperPosition);

    // Sync the two maps
    this._clearSync = syncMove(mapA, mapB);

    // Handle map resize
    this._onResize = () => {
      this._bounds = mapB.getContainer().getBoundingClientRect();
      if (this.currentPosition != null) {
        this._setPosition(this.currentPosition);
      }
    };
    mapB.on('resize', this._onResize);

    // Mousemove option
    if (this.options.mousemove) {
      mapA.getContainer().addEventListener('mousemove', this._onMove);
      mapB.getContainer().addEventListener('mousemove', this._onMove);
    }

    // Bind event listeners for dragging
    this._swiper.addEventListener('mousedown', this._onDown);
    this._swiper.addEventListener('touchstart', this._onDown);
  }

  /**
   * Internal method to toggle pointer events on the slider/control
   * @private
   * @param {string} value - Pointer events value (e.g. 'auto' or 'none')
   */
  _setPointerEvents(value) {
    this._controlContainer.style.pointerEvents = value;
    this._swiper.style.pointerEvents = value;
  }

  /**
   * Internal method to start the dragging action
   * @private
   */
  _onDown = (e) => {
    if (e.touches) {
      document.addEventListener('touchmove', this._onMove);
      document.addEventListener('touchend', this._onTouchEnd);
    } else {
      document.addEventListener('mousemove', this._onMove);
      document.addEventListener('mouseup', this._onMouseUp);
    }
  };

  /**
   * Internal method to set the position of the slider
   * @private
   * @param {number} x - Slider position in pixels from the left/top
   */
  _setPosition(x) {
    const maxPos = this._horizontal ? this._bounds.height : this._bounds.width;
    x = Math.min(x, maxPos);

    const pos = this._horizontal
      ? `translate(0, ${x}px)`
      : `translate(${x}px, 0)`;
    this._controlContainer.style.transform = pos;
    this._controlContainer.style.WebkitTransform = pos;

    const clipA = this._horizontal
      ? `rect(0, 999em, ${x}px, 0)`
      : `rect(0, ${x}px, ${this._bounds.height}px, 0)`;

    const clipB = this._horizontal
      ? `rect(${x}px, 999em, ${this._bounds.height}px, 0)`
      : `rect(0, 999em, ${this._bounds.height}px, ${x}px)`;

    this._mapA.getContainer().style.clip = clipA;
    this._mapB.getContainer().style.clip = clipB;

    this.currentPosition = x;
  }

  /**
   * Internal method to handle the pointer moving (mouse/touch)
   * @private
   */
  _onMove = (e) => {
    if (this.options.mousemove) {
      this._setPointerEvents(e.touches ? 'auto' : 'none');
    }

    if (this._horizontal) {
      this._setPosition(this._getY(e));
    } else {
      this._setPosition(this._getX(e));
    }
  };

  /**
   * Internal method for mouse up event
   * @private
   */
  _onMouseUp = () => {
    document.removeEventListener('mousemove', this._onMove);
    document.removeEventListener('mouseup', this._onMouseUp);
    this.fire('slideend', { currentPosition: this.currentPosition });
  };

  /**
   * Internal method for touch end event
   * @private
   */
  _onTouchEnd = () => {
    document.removeEventListener('touchmove', this._onMove);
    document.removeEventListener('touchend', this._onTouchEnd);
    this.fire('slideend', { currentPosition: this.currentPosition });
  };

  /**
   * Internal helper to get X position from an event
   * @private
   * @param {MouseEvent|TouchEvent} e
   * @returns {number}
   */
  _getX(e) {
    const evt = e.touches ? e.touches[0] : e;
    let x = evt.clientX - this._bounds.left;
    x = Math.max(0, Math.min(x, this._bounds.width));
    return x;
  }

  /**
   * Internal helper to get Y position from an event
   * @private
   * @param {MouseEvent|TouchEvent} e
   * @returns {number}
   */
  _getY(e) {
    const evt = e.touches ? e.touches[0] : e;
    let y = evt.clientY - this._bounds.top;
    y = Math.max(0, Math.min(y, this._bounds.height));
    return y;
  }

  /**
   * Public method to set the position of the slider programmatically
   * @param {number} x - Slider position in pixels from the left or top
   */
  setSlider(x) {
    this._setPosition(x);
  }

  /**
   * Adds a listener for events of a specified type.
   * @param {string} type - The event type to listen for (e.g. 'slideend').
   * @param {Function} listener - The function to be called when the event is fired.
   * @returns {Compare} this
   */
  on(type, listener) {
    this._ev.on(type, listener);
    return this;
  }

  /**
   * Fire an event of a specified type.
   * @param {string} type - The event type to fire (e.g. 'slideend').
   * @param {Object} data - Data passed to the event listener.
   * @returns {Compare} this
   */
  fire(type, data) {
    this._ev.emit(type, data);
    return this;
  }

  /**
   * Removes an event listener previously added with `on`.
   * @param {string} type - The event type.
   * @param {Function} listener - The function previously installed as a listener.
   * @returns {Compare} this
   */
  off(type, listener) {
    this._ev.removeListener(type, listener);
    return this;
  }

  /**
   * Removes the compare control from the DOM and cleans up event listeners
   */
  remove() {
    // Unsync the movement of the maps
    this._clearSync();
    this._mapB.off('resize', this._onResize);

    const aContainer = this._mapA.getContainer();
    if (aContainer) {
      aContainer.style.clip = null;
      aContainer.removeEventListener('mousemove', this._onMove);
    }

    const bContainer = this._mapB.getContainer();
    if (bContainer) {
      bContainer.style.clip = null;
      bContainer.removeEventListener('mousemove', this._onMove);
    }

    this._swiper.removeEventListener('mousedown', this._onDown);
    this._swiper.removeEventListener('touchstart', this._onDown);
    this._controlContainer.remove();
  }
}

// If you're working in a browser environment and want to attach Compare to mapboxgl:
if (typeof window !== 'undefined' && window.mapboxgl) {
  window.mapboxgl.Compare = Compare;
}

// If using modules (e.g. in a bundler or Node), you can export default:
export default Compare;
